import React, { useContext, useEffect } from 'react';

import NavBarBackOffice from '../components/backOffice/Navbar';
import { GlobalContext } from '../context/GlobalStateProvider';
import { pages } from '../constants/Consts';
import CommentsCard from '../components/backOffice/CommentsCard';
import { useCommentsPageData } from '../hooks/useCommentsPageData';
import { useGymSelection } from '../hooks/useGymSelection';
import GymSelectionModal from '../components/backOffice/GymSelectionModal';

const SkeletonBlock: React.FC<{ className?: string }> = ({ className = '' }) => (
    <div className={`bg-grey-200 rounded-lg animate-pulse ${className}`} />
);

const CommentsPageSkeleton: React.FC = () => (
    <>
        {/* Comments list skeleton */}
        <div className="pannels-filter__filters">
            <SkeletonBlock className="h-6 w-48" />
            <div className="flex flex-col gap-4 w-full mt-2">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex justify-between pb-4 gap-5">
                        <div className="flex flex-col gap-2 flex-1">
                            <div className="flex gap-2">
                                <SkeletonBlock className="h-12 w-12 rounded-full flex-shrink-0" />
                                <div className="flex flex-col gap-2 flex-1">
                                    <SkeletonBlock className="h-4 w-32" />
                                    <SkeletonBlock className="h-4 w-3/4" />
                                </div>
                            </div>
                            <SkeletonBlock className="h-3 w-24 ml-14" />
                        </div>
                        <div className="flex gap-6 w-[17%]">
                            <SkeletonBlock className="w-20 h-[100px] rounded-sm" />
                            <div className="flex flex-col justify-between flex-1">
                                <div className="flex flex-col gap-1">
                                    <SkeletonBlock className="h-3 w-full" />
                                    <SkeletonBlock className="h-3 w-2/3" />
                                </div>
                                <SkeletonBlock className="h-5 w-12" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </>
);

const CommentsPage: React.FC = () => {
    const globalContext = useContext(GlobalContext);
    const { currentGym, setCurrentGym, setCurrentPage, setGymsFilterInfo, gymsFilterInfo, routeSetterInfo, setRouteSetterInfo, user } = globalContext;

    const { comments, isLoading } = useCommentsPageData(user, currentGym);
    const { isModalOpen, setIsModalOpen, handleGymSelect } = useGymSelection(user, setCurrentGym, setGymsFilterInfo);

    useEffect(() => {
        document.title = "Commentaires";
    }, []);

    useEffect(() => {
        setCurrentPage(pages.commentaires);
    }, []);

    return (
        <div className='outerContainer'>
            <NavBarBackOffice
                profilePicture={routeSetterInfo?.picture}
                firstName={routeSetterInfo?.firstName}
                lastName={routeSetterInfo?.lastName}
                gyms={currentGym && currentGym.name}
            />
            <div className="bo-main-content">
                <div className="bo-main-content__title">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 4V17.17L18.83 16H4V4H20ZM20 2H4C2.9 2 2 2.9 2 4V16C2 17.1 2.9 18 4 18H18L22 22V4C22 2.9 21.1 2 20 2ZM18 12H6V14H18V12ZM18 9H6V11H18V9ZM18 6H6V8H18V6Z" fill="#1D5073"/>
                    </svg>
                    <div className="bold large3">
                        <p>Vue Commentaires</p>
                    </div>
                </div>

                {isLoading ? (
                    <CommentsPageSkeleton />
                ) : (
                    <div className="pannels-filter__filters animate-fadeIn">
                        <div>
                            <div className='bold large3 flex items-baseline'>Commentaires</div>
                        </div>
                        <div className='flex flex-col gap-4 w-full'>
                            {(comments && comments.length > 0) ?
                                comments.map((comment) =>
                                    <CommentsCard
                                        key={comment.commentId}
                                        profilePictureUser={comment.profilePictureUser}
                                        boulderId={comment.boulderId}
                                        boulderName={comment.boulderName}
                                        boulderPicture={comment.boulderPicture}
                                        comment={comment.comment}
                                        commentId={comment.commentId}
                                        createdAt={comment.createdAt}
                                        difficultySvg={comment.difficultySvg}
                                        firstNameUser={comment.firstNameUser}
                                        lastNameUser={comment.lastNameUser}
                                        level={comment.level}
                                        sectorName={comment.sectorName}
                                    />
                                )
                                :
                                <div className='no-routes-msg light large2'>
                                    Pas encore de commentaire
                                </div>
                            }
                        </div>
                    </div>
                )}
            </div>

            <GymSelectionModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                gymsFilterInfo={gymsFilterInfo}
                currentGym={currentGym}
                onGymSelect={handleGymSelect}
            />
        </div>
    );
}

export default CommentsPage;
